import { useMemo, useState } from 'react';

import {
  Button,
  ConfirmModal,
  Modal,
  SearchPanel,
  Tab,
  Tabs,
} from '../../../../components';
import PreprocessingList from './PreprocessingList';
import { CompletedList } from './CompletedList';
import CompressPromptForm from './CompressPromptForm';
import { useCompressor } from '../useCompressor';
import { CreateCompressTaskForm } from './CreateCompressTaskForm';
import type { CompressingTaskType } from '../utils/const';
import type { FormProps } from './CreateCompressTaskForm';

export type TabsType = 'analyze' | 'compress';
export type TaskProps = {
  _id: string;
  task_id: string;
  task_type: CompressingTaskType;
  status: string;
  filename: string;
  user_name: string;
  created_at: string;
  updated_at: string;
};

const TextCompressorTabs = () => {
  const {
    loading,
    reloading,
    isOpened,
    onOpen,
    onClose,
    setCreateTaskForm,
    tasksList,
    openedDeleteModal,
    openDeleteModal,
    closeDeleteModal,
    createTaskForm,
    handleCancelTask,
    handleAddTaskWithFile,
    handleAddTaskWithText,
    getTaskResultWithPromptById,
    compressingTextWithPrompt,
    setPage,
    setActiveTab,
    pagination,
  } = useCompressor();

  const [isCompressPromptOpened, setIsCompressPromptOpened] = useState(false);
  const inProgress = loading || reloading;

  const handleSaveTask = (data: FormProps) => {
    const { text, file, requested_by, chunk_size, model } = data;

    if (file) {
      handleAddTaskWithFile({
        file: file as File,
        user_name: requested_by,
        chunk_size,
        model,
      });
    } else {
      handleAddTaskWithText({
        text: text as string,
        user_name: requested_by,
        chunk_size,
        model,
      });
    }
  };

  const handleCompressClick = ({ task_id }: { task_id: string }) => {
    setCreateTaskForm({ id: task_id });
    setIsCompressPromptOpened(true);
  };

  return (
    <div className="p-4">
      <div className="flex flex-wrap justify-end items-center mb-4">
        <Button
          label="Add Task"
          onClick={() => {
            setCreateTaskForm(undefined);
            onOpen();
          }}
          size="medium"
        />
      </div>
      <ConfirmModal
        title="Do you want to delete this task?"
        open={openedDeleteModal}
        onConfirm={handleCancelTask}
        onClose={closeDeleteModal}
      />
      <Modal size="small" open={isOpened} onClose={onClose} title="New Task">
        <CreateCompressTaskForm
          key={'new task'}
          onCancel={onClose}
          saving={inProgress}
          onSave={handleSaveTask}
        />
      </Modal>

      <Modal
        size="medium"
        open={isCompressPromptOpened}
        onClose={() => setIsCompressPromptOpened(false)}
        title="Prompt"
      >
        <CompressPromptForm
          key={'prompt'}
          onCancel={() => setIsCompressPromptOpened(false)}
          saving={false}
          onSave={() => setIsCompressPromptOpened(false)}
          taskId={createTaskForm?.id || ''}
          type={createTaskForm?.type || ''}
        />
      </Modal>
      <Tabs<TabsType>
        tabsClassName="w-fit"
        tabs={[
          { name: 'analyze', label: 'Preprocessing' },
          { name: 'compress', label: 'In Progress & Completed' },
        ]}
        contentClassName="mt-md"
        onChange={tab => {
          setActiveTab(tab);
          setPage(1);
        }}
      >
        <Tab name="analyze">
          <PreprocessingList
            processingTasks={tasksList as TaskProps[]}
            loading={loading}
            actionLoading={compressingTextWithPrompt}
            handleCompressClick={handleCompressClick}
            openDeleteModal={openDeleteModal}
          />
        </Tab>
        <Tab name="compress">
          <CompletedList
            completedTasks={tasksList as TaskProps[]}
            loading={loading}
            actionLoading={compressingTextWithPrompt}
            openDeleteModal={openDeleteModal}
            handleCompressClick={handleCompressClick}
            getTaskResultWithPromptById={getTaskResultWithPromptById}
          />
        </Tab>
      </Tabs>
      <div className="mt-sm flex justify-between items-center">
        <SearchPanel
          totalItems={pagination?.totalItems}
          page={pagination?.page}
          perPage={pagination?.perPage}
          withPaginate
          setPage={setPage}
        />
      </div>
    </div>
  );
};

export default TextCompressorTabs;
